import { apiClient } from './api';

export type FarmOut = {
  id: string;
  state:
    | 'open'
    | 'awarded'
    | 'completion_reported'
    | 'withdrawn'
    | 'cancelled'
    | 'expired';
  originName: string;
  recipientName?: string | null;
  recipientCompanyId?: string | null;
  startAt: string;
  endAt: string;
  timezone: string;
  serviceDate: string;
  pickupArea: string;
  dropoffArea: string;
  requirements: string;
  publicNotes: string;
  amountCents: number;
  agreedAmountCents?: number;
  currency: string;
  priceTerms: {
    includesTolls: boolean;
    includesParking: boolean;
    includesGratuity: boolean;
    includedWaitMinutes: number;
    notes: string;
  };
  isOrigin: boolean;
  isRecipient: boolean;
  reservationId?: string;
  myInterest: string | null;
  myNegotiation?: FarmOutNegotiation | null;
  negotiations?: FarmOutNegotiation[];
  needsResponse?: boolean;
  pendingResponseCount?: number;
  attentionNeeded?: boolean;
  privateDetails?: {
    passengerName: string | null;
    pickupLocation: string;
    dropoffLocation: string;
    operatorEmail: string | null;
    operatorPhone: string | null;
  };
  interests?: { companyId: string; companyName: string; state: string }[];
  events?: {
    action: string;
    companyName: string;
    createdAt: string;
    note?: string;
    version: number;
  }[];
};
export type FarmOutNegotiation = {
  id: string;
  state:
    | 'awaiting_owner'
    | 'awaiting_provider'
    | 'awaiting_owner_confirmation'
    | 'awarded'
    | 'declined'
    | 'withdrawn'
    | 'closed';
  version: number;
  providerCompanyId?: string;
  providerName?: string;
  currentProposalId: string;
  agreedProposalId?: string | null;
  awaitingResponse: boolean;
  currentAmountCents?: number;
  agreedAmountCents?: number;
  currency?: string;
  proposals: {
    id: string;
    sequence: number;
    proposedBy: 'us' | 'them';
    proposedByName: string;
    amountCents: number;
    currency: string;
    note: string;
    createdAt: string;
  }[];
};
export type FarmOutContext = {
  eligible: boolean;
  reason: string | null;
  approved: boolean;
  reservationRef: string;
  startAt: string;
  endAt: string;
  pickupLocation: string;
  dropoffLocation: string;
  requirements: string;
  offer: FarmOut | null;
};
export type FarmOutAction =
  | 'interest'
  | 'withdraw_interest'
  | 'dismiss'
  | 'award'
  | 'withdraw'
  | 'cancel'
  | 'report_completion'
  | 'attention'
  | 'propose'
  | 'counter'
  | 'accept_counter'
  | 'confirm_agreement'
  | 'decline_negotiation'
  | 'withdraw_negotiation';
export type PublishFarmOut = {
  reservationId: string;
  requestId: string;
  pickupArea: string;
  dropoffArea: string;
  requirements: string;
  publicNotes: string;
  amountCents: number;
  currency: string;
  includesTolls: boolean;
  includesParking: boolean;
  includesGratuity: boolean;
  includedWaitMinutes: number;
  pricingNotes: string;
};
export const farmOutAPI = {
  list: (params: {
    view: string;
    date?: string;
    location?: string;
    page?: number;
    includeDismissed?: boolean;
  }) =>
    apiClient
      .get<{
        items: FarmOut[];
        total: number;
        page: number;
        limit: number;
        approved: boolean;
      }>('/admin/farm-outs', { params })
      .then((r) => r.data),
  detail: (id: string) =>
    apiClient.get<FarmOut>(`/admin/farm-outs/${id}`).then((r) => r.data),
  context: (id: string) =>
    apiClient
      .get<FarmOutContext>(`/admin/farm-outs/reservations/${id}`)
      .then((r) => r.data),
  publish: (body: PublishFarmOut) =>
    apiClient.post<FarmOut>('/admin/farm-outs', body).then((r) => r.data),
  act: (
    id: string,
    action: FarmOutAction,
    options: {
      reason?: string;
      recipientCompanyId?: string;
      negotiationId?: string;
      requestId?: string;
      amountCents?: number;
      expectedVersion?: number;
      note?: string;
    } = {}
  ) =>
    apiClient
      .post<FarmOut>(`/admin/farm-outs/${id}/actions`, {
        action,
        ...options,
      })
      .then((r) => r.data),
};
export const farmOutError = (error: any) => {
  const message = error?.response?.data?.message;
  return Array.isArray(message)
    ? message.join('. ')
    : message || 'Unable to update the farm-out. Please try again.';
};
export const farmOutStateLabel = (offer: FarmOut) =>
  offer.state === 'awarded'
    ? `Farmed out to ${offer.recipientName || 'operator'}`
    : offer.state === 'completion_reported'
    ? 'Completion reported'
    : offer.state === 'open'
    ? 'Open offer'
    : offer.state.charAt(0).toUpperCase() + offer.state.slice(1);
